import { p as proxyCustomElement, H, A as AriaAttr, h } from './p-d7c3b756.js';
import { B as BxfCardGroupConstants } from './p-04f428fe.js';
import { B as BxfI18n, t as translateKeys, i as isUsingI18n } from './p-01256821.js';
import { F as FocusIndexManager } from './p-8b298ca7.js';
import { K as KeyboardEventKey } from './p-b3de3bce.js';
import { R as Role } from './p-2c2985b9.js';

const bxfCardGroupCss = "button.sc-bxf-card-group:hover,a.sc-bxf-card-group:hover,input[type=radio].sc-bxf-card-group:hover,input[type=checkbox].sc-bxf-card-group:hover,input[type=radio].sc-bxf-card-group:checked:hover,input[type=checkbox].sc-bxf-card-group:checked:hover,svg.sc-bxf-card-group:hover{cursor:pointer}svg[disabled].sc-bxf-card-group:hover,button[disabled].sc-bxf-card-group:hover,label[disabled].sc-bxf-card-group:hover,input[type=checkbox].sc-bxf-card-group:disabled:hover,input[type=radio].sc-bxf-card-group:disabled:hover,input[type=checkbox].sc-bxf-card-group:checked:disabled:hover,input[type=radio].sc-bxf-card-group:checked:disabled:hover{cursor:default}.bxf__assistive-text--hidden.sc-bxf-card-group{position:absolute !important;left:-625rem;top:auto;width:0.0625rem;height:0.0625rem;overflow:hidden}.bxf-card-group__container.sc-bxf-card-group{padding:8px 0}";
const BxfCardGroupStyle0 = bxfCardGroupCss;

const { CARD, CARD_GROUP_CONTAINER } = BxfCardGroupConstants;
const BxfCardGroup = /*@__PURE__*/ proxyCustomElement(class BxfCardGroup extends H {
    get host() { return this; }
    _groupEl;
    //#endregion "Props"
    cardSelectedChangeHandler(event) {
        this.childCardList.forEach((card) => {
            if (event.detail.isSelected && event.detail.host === card) {
                card.isSelected = event.detail.isSelected;
                card.removeAttribute('tabindex');
            }
            else if (!this.isLinkList &&
                event.detail.isSelected &&
                !this.isMultiSelect) {
                card.isSelected = false;
                card.tabIndex = -1;
            }
            else if (event.detail.isSelected && !this.isMultiSelect) {
                card.isSelected = false;
            }
            else {
                card.removeAttribute('tabindex');
            }
        });
    }
    cardList;
    i18n;
    initI18n() {
        this.i18n = new BxfI18n(this.namespaceI18n);
        window['i18n'].on('languageChanged', () => {
            this.i18n.languageChangedHandler(this.translateText());
        });
        window['i18n'].on('loaded', () => {
            this.translateText();
        });
    }
    translateText() {
        translateKeys(this, this.i18nKeys, this.i18n);
    }
    componentWillLoad() {
        if (isUsingI18n() && this.namespaceI18n) {
            this.initI18n();
        }
    }
    componentDidLoad() {
        // Retrieves cards from slot and assigns value of isRequired prop to aria-required
        const slot = this._groupEl.querySelector('slot');
        const slottedNodes = slot?.assignedElements({ flatten: true }) || [];
        slottedNodes?.forEach((node) => {
            const cards = Array.from(node.querySelectorAll(CARD));
            cards.forEach((card) => {
                card.firstElementChild.setAttribute(AriaAttr.REQUIRED, `${this.isRequired}`);
            });
        });
    }
    disconnectedCallback() {
        this.i18n?.destroyTranslations();
    }
    constructor() {
        super();
        this.__registerHost();
        this.namespaceI18n = undefined;
        this.i18nKeys = undefined;
        this.isMultiSelect = false;
        this.isTracking = true;
        this.isRequired = false;
        this.trackingData = undefined;
        this.isLinkList = false;
        this.assistiveText = '';
        this.keyDownHandler = this.keyDownHandler.bind(this);
    }
    /**
     * Re-enable focus outline when Tab pressed
     *  Enable hover state when Space pressed
     *  Turn on active styling when enter pressed
     * @param {KeyboardEvent} event keyboard event
     */
    keyDownHandler(event) {
        if (!this.isMultiSelect && !this.isLinkList) {
            this.handleSingleSelectKeyDown(event);
        }
    }
    /**
     * Handles the keydown event for single selection within the card group.
     * Prevents default behavior for vertical navigation keys and manages focus navigation.
     *
     * @private
     * @param {KeyboardEvent} event - The keyboard event triggered by user interaction.
     */
    handleSingleSelectKeyDown(event) {
        const selectedCardIndex = this.getSelectedCardIndex();
        if (selectedCardIndex !== null) {
            if (FocusIndexManager.VERTICAL_NAV_KEYS.includes(event.key)) {
                event.preventDefault();
            }
            const nextIndex = FocusIndexManager.getFocusIndexForNavEvent(Array.from(this.childCardList), selectedCardIndex, event);
            this.handleNavigation(event, selectedCardIndex, nextIndex);
        }
    }
    /**
     * Retrieves the index of the currently selected card from the child card list.
     * A card is considered selected if it meets any of the following criteria:
     * - The card's `isSelected` property is `true`.
     * - The card is the currently active element in the document.
     * - The card is considered active by the `isCardActive` method.
     *
     * @returns {number | null} The index of the selected card, or `null` if no card is selected.
     */
    getSelectedCardIndex() {
        for (let i = 0; i < this.childCardList.length; i++) {
            if (this.childCardList[i].isSelected ||
                this.childCardList[i] === document.activeElement ||
                this.isCardActive(this.childCardList[i])) {
                return i;
            }
        }
        return null;
    }
    /**
     * Checks if a given card element is active by inspecting its computed style.
     *
     * A card is considered active if its outline property includes the color
     * 'rgb(0, 0, 243) auto'.
     *
     * @param {HTMLBxfCardElement} card - The card element to check.
     * @returns {boolean} - Returns `true` if the card is active, otherwise `false`.
     */
    isCardActive(card) {
        return window
            .getComputedStyle(card.children[0])
            .getPropertyValue('outline')
            .includes('rgb(0, 0, 243) auto ');
    }
    /**
     * Handles keyboard navigation between cards.
     *
     * @param {KeyboardEvent} event - The keyboard event that triggered the navigation.
     * @param {number} selectedCardIndex - The index of the currently selected card.
     * @param {number} nextIndex - The index of the card to navigate to.
     */
    handleNavigation(event, selectedCardIndex, nextIndex) {
        if (nextIndex !== -1) {
            this.toggleSelected(this.childCardList[selectedCardIndex], this.childCardList[nextIndex]);
        }
        if (nextIndex === -1 && event.key === KeyboardEventKey.SpaceBar) {
            this.childCardList[selectedCardIndex].isSelected = true;
        }
    }
    toggleSelected(card, nextCard) {
        card.isSelected = false;
        nextCard.isSelected = true;
        nextCard.querySelector('div').focus();
        const radioButton = nextCard.querySelector('bxf-radio');
        if (radioButton) {
            radioButton.querySelector('input').focus();
        }
    }
    get childCardList() {
        if (!this.cardList) {
            this.cardList = this.host.querySelectorAll(CARD);
        }
        if (this.isMultiSelect) {
            return this.cardList;
        }
        this.cardList.forEach((card) => {
            const radio = card.querySelector('bxf-radio');
            if (radio) {
                radio.isFocusable = !(!card.isSelected && this.cardList[0] !== card);
            }
        });
        return this.cardList;
    }
    render() {
        return (h("div", { key: 'ebbd50e241d97acac27ec75f12644c6ac53f35b7', class: CARD_GROUP_CONTAINER }, h("div", { key: 'b1053619341945a098156b3b5766a67e0e61428a', ref: (el) => (this._groupEl = el), role: this.isMultiSelect || this.isLinkList
                ? Role.GROUP
                : Role.RADIOGROUP, "aria-label": this.assistiveText, onKeyDown: this.keyDownHandler }, h("slot", { key: '847507e02e78f7e9c28d2279cc436b7697120b02' }))));
    }
    static get style() { return BxfCardGroupStyle0; }
}, [6, "bxf-card-group", {
        "namespaceI18n": [1, "namespace-i-1-8n"],
        "i18nKeys": [16],
        "isMultiSelect": [516, "is-multi-select"],
        "isTracking": [516, "is-tracking"],
        "isRequired": [516, "is-required"],
        "trackingData": [16],
        "isLinkList": [516, "is-link-list"],
        "assistiveText": [1, "assistive-text"]
    }, [[0, "cardSelected", "cardSelectedChangeHandler"]]]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bxf-card-group"];
    components.forEach(tagName => { switch (tagName) {
        case "bxf-card-group":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BxfCardGroup);
            }
            break;
    } });
}

export { BxfCardGroup as B, defineCustomElement as d };
